import graphql from "babel-plugin-relay/macro";
import { uniqBy } from "lodash";
import { useFragment } from "react-relay";

import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";
import { JoinBooking } from "@/screens/JoinBooking/types";
import { DropdownSelectOption } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { useGetGolfPlayerRateOptions_Booking$key } from "./__generated__/useGetGolfPlayerRateOptions_Booking.graphql";
import { isGolfRound } from "./useGetBundledRateOptions";

type Props = {
  round: PatronEditBooking.Round | JoinBooking.CustomerRound | null;
  holeCount: HoleCount;
  bookingRef: useGetGolfPlayerRateOptions_Booking$key | null;
};

export const useGetGolfPlayerRateOptions = ({
  round,
  holeCount,
  bookingRef,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    useGetGolfPlayerRateOptionsFragmentString,
    bookingRef
  );
  const isPrivateClub =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const rates =
    booking?.startSlot?.filteredPlayerRates?.flatMap((playerRate) => {
      if (!playerRate?.rate?.id || !playerRate.rate.name) {
        return [];
      }

      const visibility = playerRate.rate.visibility;
      const appliesToMembershipType =
        playerRate.rate.appliesToMembershipType ?? false;

      if (visibility === "NONE") {
        return [];
      }

      if (visibility === "QUALIFYING" && !appliesToMembershipType) {
        return [];
      }

      return [
        {
          id: playerRate.rate.id,
          name: playerRate.rate.name,
          nineHolePrice: playerRate.nineHolePrice ?? 0,
          eighteenHolePrice: playerRate.eighteenHolePrice ?? 0,
        },
      ];
    }) || [];

  const bookedRate = isGolfRound(round) ? round.bookedRate : null;
  const roundRate = isGolfRound(round) ? round.roundRate : null;

  // override matching playerRate with the bookedRate and roundRate
  // IF the bookedRate and roundRate are valid, then use them
  const finalRates = Array.from(rates || []).map((rr) => {
    if (bookedRate && rr?.id === bookedRate.id && bookedRate.name) {
      return bookedRate;
    }
    if (roundRate && rr?.id === roundRate.id && roundRate.name) {
      return roundRate;
    }
    return rr;
  });

  const currentRate = round && "roundRate" in round ? round.roundRate : null;

  const isCurrentRateLocked = currentRate?.isLocked ?? false;

  const singleOption: DropdownSelectOption<string> = {
    value: currentRate?.id ?? "",
    label: currentRate?.name || "",
    subLabel: isPrivateClub
      ? ""
      : `${currencySymbol}${ConvertCentsToDollars(
          holeCount === "EIGHTEEN"
            ? currentRate?.eighteenHolePrice ?? 0
            : currentRate?.nineHolePrice ?? 0
        )}`,
  };

  const dropdownOptions: DropdownSelectOption<string>[] =
    uniqBy(finalRates, "id")?.flatMap((rate) => {
      if (!rate?.id) return [];
      const priceInCents =
        holeCount === "EIGHTEEN" ? rate.eighteenHolePrice : rate.nineHolePrice;
      const priceDisplay = ConvertCentsToDollars(priceInCents ?? 0);
      return {
        value: rate.id,
        label: rate.name || "",
        subLabel: isPrivateClub ? "" : `${currencySymbol}${priceDisplay}`,
      };
    }) ?? [];

  return isCurrentRateLocked ? [singleOption] : dropdownOptions;
};

const useGetGolfPlayerRateOptionsFragmentString = graphql`
  fragment useGetGolfPlayerRateOptions_Booking on Booking {
    startSlot {
      id
      agenda {
        facility {
          club {
            supportsPrivate
          }
        }
      }
      ... on TimeSlot {
        filteredPlayerRates: playerRates(
          type: CUSTOMER
          visibleToMembershipTypeId: $membershipTypeId
        ) {
          rate {
            id
            name
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
          nineHolePrice: totalGolfPrice(holeCount: NINE)
          eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
        }
      }
    }
  }
`;
